import L from "leaflet";

const DEFAULT_MARKER_COLOR = '#6e6e6e';

export const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
};

export const roundValue = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '';

    const absValue = Math.abs(value);

    if (absValue >= 1) {
        return Math.round(value);
    }

    if (absValue === 0) return 0;

    // Per valori tra -1 e 1: prima cifra non zero con max due zeri davanti
    // Esempio: 0.0012 -> 0.001, 0.012 -> 0.01, 0.12 -> 0.1
    // Se ha più di due zeri (es 0.00012), lo arrotondiamo a un numero di decimali che mostri la prima cifra significativa, 
    // ma la specifica dice "massimo due zeri davanti", quindi se è più piccolo di 0.001 forse dovremmo gestirlo.
    // "con un massimo di due zeri davanti" suggerisce che se abbiamo 0.0001 non dovremmo mostrarlo o dovremmo fermarci a 3 decimali.
    
    const magnitude = Math.floor(Math.log10(absValue));
    const decimals = Math.max(0, -magnitude);
    
    if (decimals > 3) {
        return 0; // Se ha più di due zeri (es. 0.0001), mostriamo 0 o lo limitiamo?
        // Re-interpretazione: "con un massimo di due zeri davanti" significa 0.00X. Quindi max 3 decimali.
    }
    
    return Number(value.toFixed(decimals));
};

export const getColorForValue = (value, param, layerRegistry) => {
    if (value === undefined || value === null) return null;

    const paramConfig = layerRegistry?.params?.find(p => p.code === param);
    const legendItems = layerRegistry?.legends?.[param] || [];

    if (paramConfig?.type === 'range') {
        const item = legendItems.find(item => {
            const fromMatch = item.valueFrom === null || item.valueFrom === undefined || value >= item.valueFrom;
            const toMatch = item.valueTo === null || item.valueTo === undefined || value < item.valueTo;
            return fromMatch && toMatch;
        });
        return item?.color || null;
    }

    if (paramConfig?.type === 'category') {
        const item = legendItems.find(item => {
            return item.categories?.includes(String(value));
        });
        return item?.color || null;
    }

    return null;
};

/* --- MODEL1 --- */

export const model1Marker = ({feature, value, colorCode, textColor="#ffffff"}) => {
    const content = roundValue(value);
    return new L.divIcon({
        className: '',
        html: `<div class="marker marker-pin" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class='marker-shape marker-circle'><span>${content ?? ''}</span></div></div>`,
        iconSize: [30, 30],
        iconAnchor: [0, 0]
    });
}

export const model1Cluster = ({cluster, value, colorCode, textColor="#ffffff"}) => {
    const content = roundValue(value);

    return new L.DivIcon({
        className: '',
        html: `<div class="yetiweb-cluster" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class="yetiweb-cluster-inner"><span>${content}</span></div></div>`,
        iconSize: new L.Point(36, 36),
    });
}


/* --- MODEL23 --- */

export const model23Marker = ({feature, content, colorCode, textColor="#ffffff"}) => {
    return new L.divIcon({
        html: `<div class="marker marker-pin" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class='marker-shape marker-square'><span class="material-symbols-rounded" aria-hidden="true" style="font-size: 20px">ac_unit</span></div></div>`,
        className: '',
        iconSize: [30, 30],
        iconAnchor: [0, 0]
    })
}

export const model23Cluster = ({cluster, value, colorCode, textColor="#ffffff"}) => {
    const content = value !== undefined && value !== null ? roundValue(value) : cluster.getChildCount();

    return new L.DivIcon({
        className: '',
        html: `<div class="yetiweb-cluster" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class="yetiweb-cluster-inner"><span>${content}</span></div></div>`,
        iconSize: new L.Point(36, 36),
    });
}


/* --- INCIDENTS --- */

export const incidentMarker = ({feature, colorCode, textColor="#ffffff"}) => {
    return new L.divIcon({
        className: '',
        html: `<div class="marker marker-pin" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class='marker-shape marker-hexagon'><span class="material-symbols-rounded" aria-hidden="true" style="font-size: 20px">report</span></div></div>`,
        iconSize: [30, 30],
        iconAnchor: [0, 0]
    })
}

export const incidentCluster = ({cluster, value, colorCode, textColor="#ffffff"}) => {
    const content = value !== undefined && value !== null ? roundValue(value) : cluster.getChildCount();

    return new L.DivIcon({
        className: '',
        html: `<div class="yetiweb-cluster" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class="yetiweb-cluster-inner"><span>${content}</span></div></div>`,
        iconSize: new L.Point(36, 36),
    });
}
export const stationMarker = ({feature, colorCode, textColor="#ffffff"}) => {
    return new L.divIcon({
        className: '',
        html: `<div class="marker marker-pin-large" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class='marker-shape marker-circle'><span class="material-symbols-rounded" aria-hidden="true" style="font-size: 20px">device_thermostat</span></div>`,
        iconSize: [30, 30],
        iconAnchor: [0, 0]
    });
}

export const stationCluster = ({cluster, colorCode, textColor="#ffffff"}) => {
    const content = cluster.getChildCount();

    return new L.DivIcon({
        className: '',
        html: `<div class="yetiweb-cluster" style="--marker-color: ${colorCode ?? DEFAULT_MARKER_COLOR}; color: ${textColor}"><div class="yetiweb-cluster-inner"><span>${content}</span></div></div>`,
        iconSize: new L.Point(36, 36),
    });
}
